import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { GroupType, WorkOutcomeFragment } from "@/generated/graphql";

import { initMarkingGroups } from "./init-marking-groups";
import {
  LoadGroupsPayload,
  MarkingGroup,
  MarkingViewState,
  MarkingWorkOutcome,
} from "./types";

/** `mark` slice state */
export interface MarkSliceState {
  /** Current Marker's user ID. */
  currentMarkerId: string | null;
  /** Selected marking group ID */
  selectedMarkingGroupId: string | null;
  /** All Students and Groups and their Work Outcomes. */
  workOutcomes: MarkingWorkOutcome[];
  /**
   * Filtered QuestionBlock NODES displayed for marking. Empty array will show
   * no questions.
   */
  displayedNodeIds: string[];
  /** All Marking Groups */
  groups: MarkingGroup[];
  /** Group Setup type. This setting is only applicable in MFA Marking context. */
  groupType: GroupType | null;
  /** Switch state between personal and universal views. */
  viewState: MarkingViewState;
}

export const initialState: MarkSliceState = {
  currentMarkerId: null,
  displayedNodeIds: [],
  workOutcomes: [],
  groups: [],
  groupType: null,
  viewState: "Universal", // default to universal
  selectedMarkingGroupId: null,
};

export const markSlice = createSlice({
  name: "mark",
  initialState,
  reducers: {
    /** Initialise marking filters and groups. */
    loadMarkingGroups(state, action: PayloadAction<LoadGroupsPayload>) {
      state.groups = initMarkingGroups(action.payload);
      state.workOutcomes = action.payload.workOutcomes;
      state.groupType = action.payload.groupType;
      state.currentMarkerId = action.payload.markerId;
    },
    /** Update and sync view state when the teacher navigates to mark a different student. */
    syncViewStateForSelectedStudent(
      state,
      action: PayloadAction<string | null>
    ) {
      const studentId = action.payload;
      const currentMarkerId = state.currentMarkerId;
      const groupType = state.groupType;

      // Handle null cases - should not happen normally
      if (studentId === null || groupType === null) {
        state.selectedMarkingGroupId = null;
        state.viewState = "Universal";
        return;
      }

      // Find the relevant marking group based on group type
      let studentGroup: MarkingGroup | undefined;

      if (groupType === GroupType.Student) {
        // Student-based groups: student belongs to exactly one group
        // We select this group regardless of who the marker is
        studentGroup = state.groups.find((group) =>
          group.members.some((member) => member.id === studentId)
        );
      } else if (groupType === GroupType.Question) {
        // Question-based groups: student can be in multiple groups
        // We only select the group if the current user is the marker
        studentGroup = state.groups.find(
          (group) =>
            group.markerId === currentMarkerId &&
            group.members.some((member) => member.id === studentId)
        );
      }

      state.selectedMarkingGroupId = studentGroup?.id ?? null;

      // Update view state based on whether we found a matching group
      if (!studentGroup) {
        // No matching group found - switch to universal view
        state.viewState = "Universal";
      } else if (studentGroup.markerId === currentMarkerId) {
        // Current user is the marker for this group - switch to personal view
        state.viewState = "Personal";
      } else {
        // Group found but current user is not the marker - stay in universal view
        state.viewState = "Universal";
      }
    },
    setDisplayedNodeIds(state, action: PayloadAction<string[]>) {
      state.displayedNodeIds = action.payload;
    },
    /** Set marker view level. */
    setViewState(state, action: PayloadAction<"Personal" | "Universal">) {
      state.viewState = action.payload;
    },
    setSelectedMarkingGroupId(state, action: PayloadAction<string>) {
      state.selectedMarkingGroupId = action.payload;
    },
    updateWorkOutcomeScoreBreakdown(
      state,
      action: PayloadAction<{
        workOutcomeId: string;
        scoreBreakdown: WorkOutcomeFragment["scoreBreakdown"];
      }>
    ) {
      const { workOutcomeId, scoreBreakdown } = action.payload;
      state.workOutcomes = state.workOutcomes.map((work) =>
        work.id === workOutcomeId ? { ...work, scoreBreakdown } : work
      );
    },
  },
});

export const {
  loadMarkingGroups,
  syncViewStateForSelectedStudent,
  setDisplayedNodeIds,
  setViewState,
  setSelectedMarkingGroupId,
  updateWorkOutcomeScoreBreakdown,
} = markSlice.actions;

export const markReducer = markSlice.reducer;
